import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { ModalDialog, ButtonBar, FlowForm, getKeyHandler } from 't-lib-ui/structure';
import { TextButton } from 't-lib-ui/widgets';
import { FetchError } from 't-lib-ui/connector';
import { AppState } from '../context/general';
import { postReport } from '../connector/api';
import { getEmail, getCachedEmail } from '../connector/auth';
import { REPORT_REASONS, ReportReason } from '../schema-copy/api-schema';
import TurnstileWidget, { isTurnstileConfigured } from './TurnstileWidget';
import './ReportPopup.css';

/** Target of a report: a feed, or a specific post within a feed. */
export interface ReportTarget {
  feedId: string;
  /** Present when reporting a specific post. */
  postIndex?: number;
  /** Feed or post title, shown for confirmation. */
  title?: string;
}

export const REASON_LABELS: Record<ReportReason, string> = {
  COPYRIGHT: 'Copyright infringement / piracy',
  CSAM: 'Child sexual abuse material',
  SEXUAL: 'Illegal or prohibited pornography',
  ILLEGAL: 'Other illegal content',
  SPAM: 'Spam or misleading',
  OWNERSHIP: "I'm the owner - please contact me",
  TECHNICAL: 'Technical problem',
  OTHER: 'Something else',
};

/** Reasons where we need a way to reach the reporter, so the email field is always shown. */
const CONTACT_REASONS: ReportReason[] = ['OWNERSHIP', 'TECHNICAL'];

const REASON_SELECTIONS = [
  { id: '', text: 'Select a reason…' },
  ...REPORT_REASONS.map(r => ({ id: r, text: REASON_LABELS[r] })),
];

const DETAILS_MAX = 2000;

export function useReport(): { report: (target: ReportTarget) => void; reportPopup: React.ReactNode } {
  const [target, setTarget] = useState<ReportTarget | null>(null);

  const report = useCallback((t: ReportTarget) => setTarget(t), []);

  const reportPopup = (
    <ReportPopup
      show={!!target}
      target={target}
      onClose={() => setTarget(null)}
    />
  );

  return { report, reportPopup };
}

interface ReportPopupProps {
  show: boolean;
  target: ReportTarget | null;
  onClose: () => void;
}

const ReportPopup: React.FC<ReportPopupProps> = ({ show, target, onClose }) => {
  const appState = useContext(AppState);
  const auth = appState.state.authData;
  const isLoggedIn = appState.state.loginState == 'ok';

  const [reason, setReason] = useState<ReportReason | ''>('');
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const [turnstileReset, setTurnstileReset] = useState(0);

  // True once the reporter typed in the email field, so a late prefill never overwrites their input.
  const emailTouched = useRef(false);

  // Reset the form whenever the dialog (re)opens.
  useEffect(() => {
    if (!show) return;
    setReason('');
    setDetails('');
    setSubmitting(false);
    setDone(false);
    setError(null);
    setTurnstileToken(undefined);

    // Prefill the contact address for logged-in reporters, from the same sources as the Account
    // page: the cached user record or the in-memory decrypted email, both free. Only when neither
    // holds it do we decrypt the stored blob via /auth/email (one request, and it may fail on iOS
    // PWAs that drop the authToken cookie — then the field simply stays empty).
    emailTouched.current = false;
    const known = isLoggedIn ? appState.state.currentUser?.email || getCachedEmail() : undefined;
    setEmail(known || '');
    if (!known && isLoggedIn && auth)
      getEmail(auth).then(e => { if (e && !emailTouched.current) setEmail(e); }).catch(() => {});
  }, [show, target?.feedId, target?.postIndex]);

  const isPost = target?.postIndex != null;
  const needsContact = !!reason && CONTACT_REASONS.includes(reason);
  // Anonymous reporters must pass Turnstile (the server rejects tokenless anonymous reports).
  const needsTurnstile = !isLoggedIn;

  const handleSubmit = useCallback(async () => {
    if (!target || !reason || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await postReport({
        feedId: target.feedId,
        postIndex: target.postIndex,
        reason,
        details: details.trim() || undefined,
        reporterEmail: email.trim() || undefined,
        turnstileToken,
      }, auth);
      setDone(true);
      setTimeout(onClose, 2000);
    } catch (e) {
      if (e instanceof FetchError && e.type == 'TurnstileFailed') {
        setError(isTurnstileConfigured()
          ? 'Verification failed. Please complete the check below and try again.'
          : 'Verification is unavailable: this build has no Turnstile sitekey configured.');
        setTurnstileToken(undefined);
        setTurnstileReset(r => r + 1);
      }
      else
        setError(e instanceof Error ? e.message : 'Could not send the report. Please try again.');
      setSubmitting(false);
    }
  }, [target, reason, details, email, turnstileToken, auth, submitting, onClose]);

  return (
    <ModalDialog
      show={show}
      title={isPost ? 'Report this post' : 'Report this feed'}
      closeButton="close"
      onClose={onClose}
      className="reportPopup"
      minWidth="420px"
    >
      {done ? (
        <p className="reportPopupSuccess">Thank you. Our team will review this {isPost ? 'post' : 'feed'}.</p>
      ) : (
        <>
          <p className="reportPopupNote">
            Flag <span className="reportPopupTarget">{target?.title || (isPost ? 'this post' : 'this feed')}</span> for
            the Downread team, because it may contain illegal or abusive content, to claim ownership, or to report a
            technical problem. Reports go to the team for review.
          </p>
          <FlowForm>
            <div className="reportPopupField">
              <label className="reportPopupLabel" htmlFor="reportReason">Reason</label>
              <select
                id="reportReason"
                className="reportPopupSelect flowFocussable"
                value={reason}
                onChange={(e) => setReason(e.target.value as ReportReason | '')}
              >
                {REASON_SELECTIONS.map(s => <option key={s.id} value={s.id}>{s.text}</option>)}
              </select>
            </div>
            <div className="reportPopupField">
              <label className="reportPopupLabel" htmlFor="reportDetails">Details (optional)</label>
              <textarea
                id="reportDetails"
                className="reportPopupTextArea flowFocussable"
                value={details}
                maxLength={DETAILS_MAX}
                rows={3}
                placeholder="Anything that helps us review this faster (links, what you saw)…"
                onChange={(e) => setDetails(e.target.value)}
                onKeyDown={getKeyHandler({ multiLine: true })}
              />
            </div>
            {(!isLoggedIn || needsContact) && (
              <div className="reportPopupField">
                <label className="reportPopupLabel" htmlFor="reportEmail">
                  {needsContact ? 'Your email (so we can reach you)' : 'Your email (optional)'}
                </label>
                <input
                  id="reportEmail"
                  type="email"
                  className="reportPopupInput flowFocussable"
                  value={email}
                  maxLength={100}
                  placeholder="So we can follow up if needed"
                  onChange={(e) => { emailTouched.current = true; setEmail(e.target.value); }}
                  onKeyDown={getKeyHandler({ sidewaysBehaviour: 'rightAtEnd', enterBehaviour: 'next' })}
                />
              </div>
            )}
            {needsTurnstile && show && (
              <TurnstileWidget action="report" onToken={setTurnstileToken} resetSignal={turnstileReset} />
            )}
            {error && <p className="reportPopupError">{error}</p>}
            <ButtonBar className="full-margin-top" align="right">
              <TextButton type="minor" onClick={onClose} label="Cancel" />
              <TextButton
                type="major"
                focussable="sideway"
                onClick={handleSubmit}
                label={submitting ? 'Sending…' : 'Send report'}
                enabled={reason && !submitting && (!needsTurnstile || turnstileToken) ? 'enabled' : 'disabled'}
              />
            </ButtonBar>
          </FlowForm>
        </>
      )}
    </ModalDialog>
  );
};

export default ReportPopup;
